import { keys, reduce } from 'lodash-es';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useInterval(callback: () => void, delay: number) {
    const savedCallback = useRef(callback);

    // Remember the latest callback if it changes.
    useLayoutEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        const id = setInterval(() => savedCallback.current(), delay);

        return () => clearInterval(id);
    }, [delay]);
}

interface UploadProgressReturn {
    uploadProgress: number;
    trackFileProgress: (fileName: string, fileProgress: number) => void;
    resetProgress: () => void;
}

export const useUploadProgress = (): UploadProgressReturn => {
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const fileTracker = useRef<Record<string, number>>({});

    const resetProgress = (): void => {
        fileTracker.current = {};
        setUploadProgress(null);
    };

    const trackFileProgress = (fileName: string, fileProgress: number): void => {
        fileTracker.current[fileName] = fileProgress;

        const numberOfFilesBeingUploaded = keys(fileTracker.current).length;
        const newUploadProgress =
            reduce(fileTracker.current, (result, value): number => (result += value), 0) / numberOfFilesBeingUploaded;

        if (uploadProgress !== newUploadProgress) {
            setUploadProgress(newUploadProgress);
        }
    };

    return { uploadProgress, trackFileProgress, resetProgress };
};
